import { currentUrl, historyEvent, HistoryEventType, replaceState, translate } from '@avensia/excite';
import {
  type PropType,
  type Selector,
  SelectType,
  type OptionType,
  useVariantSelector,
  DefaultSelectorId,
} from './ProductVariantsSelectorProvider';
import * as React from 'react';
import { useCustomerType } from '@avensia/excite-commercetools';

import type { JSX } from 'react';

const BUNDLE_SELECTED_PART_URL_PARAMS = 'selected_part';
function getInitialSelection(
  packagePart: NordicNest.Api.PackagePartApiModel,
  variants: NordicNest.Api.VariantLinkApiModel[],
) {
  const current = currentUrl();
  const parts = current.searchParams.getAll(BUNDLE_SELECTED_PART_URL_PARAMS);
  const regex = /(.+)__(.+)/;
  try {
    const newSelectedParts = parts.map(p => {
      const matches = regex.exec(p);
      if (!matches) {
        throw Error('invalid part format: ' + p);
      }
      const packagePartKey = matches[1];
      const sku = matches[2];
      const variant = variants.find(v => v.articleNumber === sku && v.packagePartKey === packagePartKey);
      if (!variant) {
        throw Error('unable to find variant');
      }
      return {
        packagePartKey,
        sku,
        variant,
      };
    });
    let sku = newSelectedParts.find(p => p.packagePartKey === packagePart.key)?.sku;
    if (!sku) {
      sku = packagePart.isOptional
        ? undefined
        : variants.find(v => v.packagePartKey === packagePart.key)?.articleNumber;
    }

    return sku;
  } catch {}
  return variants.find(v => v.packagePartKey === packagePart.key)?.articleNumber;
}

function ensureUrlContainsPackageParts(newSelectedParts: { packagePartKey: string; sku: string }[]) {
  const url = currentUrl();
  const newParts = newSelectedParts.map(s => `${s.packagePartKey}__${s.sku}`);
  newParts.sort();
  url.searchParams.delete(BUNDLE_SELECTED_PART_URL_PARAMS);
  for (const part of newParts) {
    url.searchParams.append(BUNDLE_SELECTED_PART_URL_PARAMS, part);
  }
  if (url.href !== currentUrl().href) {
    replaceState(url, { skipLoad: true });
  }
}

export function useBundleVariantSelector(
  props: React.PropsWithChildren<PropType>,
  addOrUpdate: (selector: Selector) => void,
  variantTransform: (option: OptionType, isSelectedValue: boolean, placeholder?: string) => JSX.Element,
) {
  const { variants, packageKey, packageParts } = props;
  const [selected, setSelected] = React.useState<{ [k: string]: string }>(() => {
    if (!packageKey || !packageParts?.length) return {};
    return initialSelectOptions(packageParts, variants);
  });

  React.useEffect(
    () =>
      historyEvent.on(event => {
        if (packageKey && packageParts?.length && event.event !== HistoryEventType.Replace) {
          setSelected(initialSelectOptions(packageParts, variants));
        }
      }),
    [packageKey, packageParts, variants],
  );

  const selectors = React.useMemo(() => {
    if (!packageKey || !packageParts?.length) {
      return [];
    }
    return packageParts.map(pp => {
      const optionVariants = variants.filter(v => v.packagePartKey === pp.key);
      const selectedValue = selected[pp.key] ?? getInitialSelection(pp, props.variants);
      const selectedVariant = variants.find(v => v.articleNumber === selectedValue);
      const options: OptionType[] = optionVariants.map(v => ({
        value: v.articleNumber,
        text: v.name,
        variant: {
          ...v,
          price: {
            inclVat: v.price.inclVat - (selectedVariant?.price.inclVat ?? 0),
            exclVat: v.price.exclVat - (selectedVariant?.price.exclVat ?? 0),
            vatPercent: v.price.vatPercent,
          },
        },
        showPlusSign: v.price.inclVat > (selectedVariant?.price.inclVat ?? 0),
      }));

      const prefix = `${pp.quantity} x ${pp.displayName}`;
      const selector: Selector = {
        showVariantSelect: true,
        id: pp.key,
        value: selectedValue,
        options,
        type: SelectType.Bundle,
        selectedOption: selectedVariant && { text: selectedVariant.name, value: selectedVariant.articleNumber },
        additionalPrice: selectedVariant?.price ?? { inclVat: 0, exclVat: 0, vatPercent: 0 },
        selectedVariant: selectedVariant,
        placeholder: translate(t => t.product.chooseVariant),
        onChangeOption: value =>
          setSelected(p => {
            const newSelected = { ...p, [pp.key]: value };
            ensureUrlContainsPackageParts(
              Object.entries<string>(newSelected)
                .filter(e => !!e[1])
                .map(e => ({ packagePartKey: e[0], sku: e[1] })),
            );
            return newSelected;
          }),
        optionTransform: o => variantTransform(o, false),
        selectedTransform: o => variantTransform(o, true, prefix),
      };
      return selector;
    });
  }, [packageKey, packageParts, props.variants, selected, variantTransform, variants]);
  for (const selector of selectors) {
    addOrUpdate(selector);
  }
}

function initialSelectOptions(
  packageParts: NordicNest.Api.PackagePartApiModel[],
  variants: NordicNest.Api.VariantLinkApiModel[],
) {
  const selected: { [k: string]: string } = {};
  packageParts.forEach(pp => {
    const initial = getInitialSelection(pp, variants);
    if (initial) {
      selected[pp.key] = initial;
    }
  });
  return selected;
}

export function getSelectedParts(selectors: Selector[]) {
  return selectors
    .filter(s => s.id !== DefaultSelectorId && !!s.value)
    .map(s => {
      return { packagePartKey: s.id, sku: s.value } as Excite.Commercetools.PackagePartRequest;
    });
}

export function useCalcPrice(productPrice: NordicNest.Api.PriceApiModel, packageKey?: string) {
  const { selectors } = useVariantSelector();
  const { isCompanyCustomer } = useCustomerType();

  const selectedSelectors = selectors.filter(s => s.value !== '');
  const additionalPrice = selectedSelectors
    .map(s => (isCompanyCustomer ? s.additionalPrice.exclVat : s.additionalPrice.inclVat))
    .reduce((a, b) => a + b, 0);
  const regularPrice = selectedSelectors
    .map(s => (isCompanyCustomer ? s.selectedVariant?.regularPrice.exclVat : s.selectedVariant?.regularPrice.inclVat))
    .reduce((a, b) => (a ?? 0) + (b ?? 0), 0);

  let price: NordicNest.Api.PriceApiModel;
  if (packageKey) {
    const priceIsQualifiedForOriginalPrice =
      productPrice.regular?.inclVat &&
      productPrice.regular?.inclVat !== 0 &&
      productPrice.showComparePrice &&
      productPrice.showComparePriceType === 'originalPrice' &&
      (productPrice.current.inclVat > productPrice.regular.inclVat + productPrice.minimumOriginalSaveAmount ||
        (1 - productPrice.current.inclVat / productPrice.regular.inclVat) * 100 >=
          productPrice.minimumOriginalSavePercentage);
    price = {
      ...productPrice,
      regular: priceIsQualifiedForOriginalPrice ? { inclVat: regularPrice, exclVat: regularPrice } : undefined,
      current: { inclVat: additionalPrice, exclVat: additionalPrice },
    } as NordicNest.Api.PriceApiModel;
  } else {
    price = {
      ...productPrice,
      current: {
        inclVat: productPrice.current.inclVat + additionalPrice,
        exclVat: productPrice.current.exclVat + additionalPrice,
      },
    } as NordicNest.Api.PriceApiModel;
  }

  return { price };
}

export function useCalcDeliveryTime(packageParts: NordicNest.Api.PackagePartApiModel[] | undefined): {
  packageSlowestVariantDeliveryTime: NordicNest.Api.VariantLinkApiModel;
  packageWorstPartStatus: NordicNest.Api.ProductStatus | undefined;
  packageWorstStockStatus: NordicNest.Api.StockStatus | undefined;
} {
  const { selectors } = useVariantSelector();
  const selectedVariants = selectors
    .map(t => t.selectedVariant)
    .filter(t => Boolean(t)) as NordicNest.Api.VariantLinkApiModel[];

  const packageSlowestVariantDeliveryTime =
    selectedVariants.find(v => v.deliveryTime === -1) ??
    selectedVariants.sort((a, b) => b.deliveryTime - a.deliveryTime)[0];

  const packageWorstPartStatus = packageParts?.filter(p => p.hasWorstAvailabilityInPackage)[0].partStatus;
  const packageWorstStockStatus = packageParts?.filter(p => p.hasWorstAvailabilityInPackage)[0].stockStatus;

  return {
    packageSlowestVariantDeliveryTime,
    packageWorstPartStatus,
    packageWorstStockStatus,
  };
}
